import { Keypair, hash } from "@stellar/stellar-sdk";
import { Buffer } from "buffer";
import { hex, sha256, utf8 } from "./encoding";
import type { FreighterAuthentication } from "./storage";

/** Domain-bound text Freighter shows the user; the same text always yields the same key. */
export function freighterKeyMessage(scope: string, origin: string): string {
  return [
    "StealthOS private wallet unlock v1",
    "Only approve this on " + origin,
    "Wallet: " + scope,
    "Signing this message opens your encrypted Stellar private wallet. It does not move funds.",
  ].join("\n");
}

export async function freighterKeyDigest(message: string): Promise<string> {
  return hex(await sha256(utf8(message)));
}

function verified(source: string, message: string, signature: Uint8Array) {
  const key = Keypair.fromPublicKey(source);
  const sig = Buffer.from(signature);
  const raw = Buffer.from(message, "utf8");
  // Freighter signs the SEP-53 prefixed hash; older builds signed the message bytes.
  return (
    key.verify(
      hash(Buffer.concat([Buffer.from("Stellar Signed Message:\n"), raw])),
      sig,
    ) || key.verify(raw, sig)
  );
}

export interface FreighterSigner {
  signMessage: (
    message: string,
    opts?: { networkPassphrase?: string; address?: string },
  ) => Promise<any>;
}

/** Ed25519 signatures are deterministic, so one Freighter approval re-derives the vault key. */
export async function freighterWalletKey(
  signer: FreighterSigner,
  source: string,
  scope: string,
  networkPassphrase: string,
): Promise<{ password: string; authentication: FreighterAuthentication }> {
  const origin = location.origin;
  const message = freighterKeyMessage(scope, origin);
  const result = await signer.signMessage(message, {
    address: source,
    networkPassphrase,
  });
  if (!result || result.error)
    throw Error(
      "Freighter did not approve the wallet unlock. Approve the message in Freighter to open your private wallet.",
    );
  if (result.signerAddress && result.signerAddress !== source)
    throw Error("Freighter signed with a different account. Reconnect and retry.");
  const signed = result.signedMessage;
  const signature =
    typeof signed === "string"
      ? Uint8Array.from(Buffer.from(signed, "base64"))
      : signed instanceof Uint8Array
        ? Uint8Array.from(signed)
        : signed?.data
          ? Uint8Array.from(signed.data)
          : new Uint8Array();
  if (signature.length !== 64 || !verified(source, message, signature))
    throw Error(
      "Freighter returned an unverifiable signature. No wallet was created or changed.",
    );
  const password = hex(
    await sha256(new Uint8Array([...utf8("StealthOS Stellar vault key v1\0"), ...signature])),
  );
  signature.fill(0);
  return {
    password,
    authentication: {
      type: "freighter-signature",
      signerAddress: source,
      messageDigest: await freighterKeyDigest(message),
      origin,
    },
  };
}
